import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { PhotoPicker, Section, inputCls, newKey } from "./shared";

const EMPTY = { text: "", name: "", occasion: "", photo_id: "" };

export function ReviewsSection({ testimonials, photos, update, saving, onSave }) {
  const [draft, setDraft] = useState(EMPTY);

  const addReview = () => {
    if (!draft.text.trim() || !draft.name.trim()) return toast.error("Add the review text and customer name");
    update((c) =>
      c.testimonials.push({ ...draft, text: draft.text.trim(), name: draft.name.trim(), occasion: draft.occasion.trim(), _key: newKey() })
    );
    setDraft(EMPTY);
  };

  return (
    <Section title="Reviews" hint="These rotate in the Kind Words section near the top of the page." section="reviews" saving={saving} onSave={onSave}>
      <div className="space-y-3">
        {testimonials.map((t, i) => (
          <ReviewRow key={t._key} review={t} index={i} photos={photos} update={update} />
        ))}
        <div className="rounded-xl border border-dashed border-gold/50 p-3">
          <textarea
            rows={2}
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            placeholder="Review text…"
            data-testid="review-new-text"
            className={`${inputCls} w-full`}
          />
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Customer name" data-testid="review-new-name" className={inputCls} />
            <input value={draft.occasion} onChange={(e) => setDraft({ ...draft, occasion: e.target.value })} placeholder="Occasion (e.g. Wedding cake)" data-testid="review-new-occasion" className={inputCls} />
            <button
              onClick={addReview}
              data-testid="review-add-button"
              className="flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gold bg-gold px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-golddeep"
            >
              <Plus className="h-3.5 w-3.5" /> Add review
            </button>
          </div>
          <div className="mt-2">
            <PhotoPicker photos={photos} value={draft.photo_id} onChange={(v) => setDraft({ ...draft, photo_id: v })} testId="review-new-photo" />
          </div>
          <p className="mt-2 text-xs text-plum/50">Link a cake photo so visitors can see what was praised. Remember to press Save after adding.</p>
        </div>
      </div>
    </Section>
  );
}

function ReviewRow({ review, index, photos, update }) {
  const byKey = (c) => c.testimonials.find((x) => x._key === review._key);
  return (
    <div className="flex items-start gap-3 rounded-xl border border-gold/25 bg-blush/60 p-3" data-testid={`review-item-${index}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm italic text-plum/85">“{review.text}”</p>
        <p className="mt-1 text-xs text-plum/55">
          <span className="font-semibold text-wine">{review.name}</span> · {review.occasion}
        </p>
        <div className="mt-2">
          <PhotoPicker photos={photos} value={review.photo_id} onChange={(v) => update((c) => (byKey(c).photo_id = v))} testId={`review-photo-${index}`} />
        </div>
      </div>
      <button
        onClick={() => update((c) => (c.testimonials = c.testimonials.filter((x) => x._key !== review._key)))}
        aria-label="Delete review"
        data-testid={`review-delete-${index}`}
        className="shrink-0 rounded-lg p-1.5 text-plum/40 transition-colors hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
